import { Suspense } from 'react';

import { useQuery } from '@apollo/client/react';
import { Alert, Box, Button, Paper, Stack, Typography } from '@mui/material';
import { type GridRowsProp } from '@mui/x-data-grid-premium';
import {
  Link,
  Route,
  Routes,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import { gql } from '@graphql/__generated__';
import { type GetListingOffersQuery } from '@graphql/__generated__/graphql';
import { OffersTable } from '@modules/_shared/components/offers/OffersTable';
import { GET_LISTING_BUYERS_DASHBOARD } from '@modules/listings/listingBuyersDashboardQueries';
import { OfferDrawer } from '@modules/offers/OfferDrawer';
import { useLocale } from '@shared/hooks/locale';

export const GET_LISTING_OFFERS_QUERY = gql(/* GraphQL */ `
  query GetListingOffers($listingId: uuid!) {
    offers(
      where: { lot_id: { _eq: $listingId } }
      order_by: { made_on: desc }
    ) {
      id
      amount
      valid_until
      lot {
        id
        currency
        broker {
          ...UserInfo_user
        }
      }
      status
      offer_refused_type {
        id
        label
      }
      made_on
      comments
      buyer {
        ...UserInfo_user
      }
      created_by
      ...ChangeOfferStatusModal
    }
  }
`);

type OfferRow = GetListingOffersQuery['offers'][number];

export const ListingOffers: React.FC<{ readOnly?: boolean }> = ({
  readOnly = false,
}) => {
  const { lotId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useLocale();

  const { data, loading, error } = useQuery(GET_LISTING_OFFERS_QUERY, {
    skip: lotId == null,
    variables: { listingId: lotId ?? '' },
  });

  if (error != null) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        <pre>{JSON.stringify(error, null, 2)}</pre>
      </Alert>
    );
  }

  const rows: GridRowsProp<OfferRow> = data?.offers ?? [];

  return (
    <>
      <Stack
        spacing={2}
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
        }}
      >
        <Typography variant="h6">
          {t('Offers ({{offersCount}})', {
            offersCount: rows.length ?? 0,
          })}
        </Typography>

        {!readOnly && (
          <Button
            variant="contained"
            component={Link}
            to={{
              pathname: 'new',
              search: searchParams.toString(),
            }}
            disableElevation
          >
            {t('New')}
          </Button>
        )}
      </Stack>
      <Box
        sx={{
          px: { sm: 0, md: 2 },
        }}
      >
        <Paper
          variant="outlined"
          sx={theme => ({
            overflow: 'hidden',
            [theme.breakpoints.down('md')]: { borderRadius: 0 },
          })}
        >
          <OffersTable
            rows={rows}
            isLoading={loading}
            fromListing
            onRowClick={({ id }) => {
              navigate({
                pathname: `./${id}`,
                search: searchParams.toString(),
              });
            }}
          />
        </Paper>
      </Box>
      <Suspense fallback={null}>
        <Routes>
          {!readOnly && (
            <Route
              path="new"
              element={
                <OfferDrawer
                  refetchQueries={[
                    GET_LISTING_OFFERS_QUERY,
                    ...(lotId != null
                      ? [
                          {
                            query: GET_LISTING_BUYERS_DASHBOARD,
                            variables: { id: lotId },
                          },
                        ]
                      : []),
                  ]}
                />
              }
            />
          )}
          <Route path=":offerId" element={<OfferDrawer />} />
        </Routes>
      </Suspense>
    </>
  );
};
